/**
 * Eid al-Adha cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidAlAdhaCardsPack: ToilePack = {
    slug: 'eid-al-adha-cards',
    category: 'season',
    name: 'Eid al-Adha cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'mubarak', label: 'Eid Mubarak', subject: 'a vertical card lettered "Eid al-Adha Mubarak" beneath a crescent moon and three hanging lanterns, a blank panel below for a message'},
        {key: 'kurban', label: 'Kurban Bayramı', subject: 'a card reading "Kurban Bayramınız Mübarek Olsun" in an arch-shaped panel framed by a mosque dome and two minarets'},
        {key: 'lamb', label: 'Lamb card', subject: 'a children\'s card with a smiling white lamb wearing a flower garland under a crescent moon, "Happy Eid" in round letters across the top'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor illustration, loose edges, hand-lettered text', palette: 'emerald, gold and midnight blue washes on white paper', framing: 'Centered on the page with a wide white margin'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'emerald, gold, deep blue and cream, a limited palette', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'papercut', label: 'Paper cut', style: 'layered paper-cut look, soft drop shadow between layers, scalloped edges', palette: 'deep green, gold and ivory paper layers', framing: 'Layered artwork centered with an ivory margin'},
    ],
    directions: ['Arabic script', 'Turkish', 'Gold crescent'],
    directionHint: 'try: Arabic script',
    seo: {
        title: 'Eid al-Adha Cards: Mubarak, Kurban Bayramı, Lamb',
        metaDescription: 'Printable Eid al-Adha cards with the greeting spelled exactly: Eid Mubarak, Kurban Bayramı and a lamb card, in three styles.',
        h1: 'Eid al-Adha cards: Eid Mubarak, Kurban Bayramı, lamb',
        tagline: 'Nine printable cards with the greeting spelled exactly and an empty panel for a message.',
        intro: 'Printable cards for family and neighbours at Eid al-Adha, the feast of the sacrifice at the close of the Hajj, called Bakra Eid in Pakistan, Eid-e Qurban in Iran and Kurban Bayramı in Turkey. Each card carries its greeting spelled exactly: Eid al-Adha Mubarak under a crescent, Kurban Bayramınız Mübarek Olsun in an arch, or Happy Eid over a lamb. The message panel is left blank. Type a different greeting, and the same card carries it.',
        useCases: [
            'a card for family at Eid al-Adha',
            'a card for neighbours',
            'a card a child gives a grandparent',
            'a card for a weekend school',
        ],
        faq: [
            {q: 'Which greeting is in which language?', a: 'The first two cards are in English, and the Kurban card is in Turkish: Kurban Bayramınız Mübarek Olsun.'},
            {q: 'What is the paper-cut style?', a: 'Layered paper with soft shadows between the layers and scalloped edges, in deep green, gold and ivory.'},
            {q: 'Can the greeting be changed?', a: 'Yes. Type a different greeting, and the same card layout carries it.'},
        ],
    },
}
